import { Box, Typography } from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useInputWithDebounce } from '../../../../../../../customHook/useInputWithDebounce';
import { useImagePreview } from '../../../../../../../customHook/useProfilePicture';
import { CompleteOperationType, StartOperationType } from '../../../../../../../model/alerts.model';
import { Contact, DrawerType } from '../../../../../../../model/contacts.model';
import { HuddleClientService } from '../../../../../../../service/huddle-client.service';
import zeplinColor from '../../../../../../../theme/Color';
import { validators } from '../../../../../../../utilities/utilities';
import CustomButton from '../../../../../CustomButton/CustomButton';
import CustomDatePicker from '../../../../../CustomDatePicker/CustomDatePicker';
import CustomTextField from '../../../../../CustomTextField/CustomTextField';

type FormField = {
  label: string;
  required: boolean;
  name: string;
};

export const fields: FormField[] = [
  {
    label: 'Contact name',
    required: true,
    name: 'name',
  },
  {
    label: 'Account name',
    required: false,
    name: 'accountName',
  },
  {
    label: 'Company name',
    required: true,
    name: 'company',
  },
  {
    label: 'Email',
    required: true,
    name: 'email',
  },
  {
    label: 'Mobile',
    required: false,
    name: 'phone',
  },
  {
    label: 'Relationship',
    required: false,
    name: 'roles',
  },
];

export function ContactForm({
  type,
  data,
  fetchContacts,
  handleToggleDrawer,
  startOperation,
  completeOperation,
}: {
  type: string;
  data: Contact | undefined;
  fetchContacts?: (accountId: string, isEditing?: boolean) => Promise<void>;
  handleToggleDrawer: (type?: DrawerType) => void;
  startOperation: StartOperationType;
  completeOperation: CompleteOperationType;
}) {
  const client = useLocation();
  const accountId = client.state.client.AccountId;
  const isEditMode = type === 'Edit Contact';
  const profilePicture = data?.profileImageUrl;
  const [birthdate, setBirthdate] = useState<Dayjs | null>(
    isEditMode && data && data.birthdate ? dayjs(data.birthdate) : null,
  );
  const {
    fileInputRef,
    previewUrl,
    handleFileChange,
    clearSelection,
    openFileSelection,
    uploadProfilePicture,
  } = useImagePreview(isEditMode ? profilePicture : null);
  const nameInput = useInputWithDebounce(
    isEditMode && data ? data.name : '',
    500,
    validators.validateName,
  );
  const companyInput = useInputWithDebounce(
    isEditMode && data ? data.company : '',
    500,
    validators.validateCompany,
  );
  const emailInput = useInputWithDebounce(
    isEditMode && data ? data.email : '',
    500,
    validators.validateEmail,
  );
  const phoneInput = useInputWithDebounce(
    isEditMode && data ? data.phone : '',
    500,
    validators.validatePhone,
  );
  const rolesInput = useInputWithDebounce(
    isEditMode && data ? data.roles : '',
    500,
    validators.validateRoles,
  );
  const accountName = useInputWithDebounce(
    isEditMode && data ? data.accountName : '',
    500,
    validators.validateAccountName,
  );

  const inputHooks = {
    name: nameInput,
    company: companyInput,
    email: emailInput,
    phone: phoneInput,
    roles: rolesInput,
    accountName: accountName,
  };

  const isFormValid = fields.every((field) => {
    const fieldRequired = field.required;
    const hook = inputHooks[field.name as keyof typeof inputHooks];
    if (!fieldRequired) return true;
    if (!hook || !hook.value || !hook.isValid || hook.value.trim() === '') return false;
    return true;
  });

  const handleEditContact = async () => {
    if (data === undefined) return;
    try {
      startOperation?.('rename', 'Editing contact details...');
      const payload: Record<string, string> = {};

      let uploadedUrl: string | null = null;
      if (previewUrl) {
        const result = await uploadProfilePicture();
        uploadedUrl = result === undefined ? null : result;
      }

      for (const [key, hook] of Object.entries(inputHooks)) {
        const originalValue = isEditMode ? ((data as any)[key] ?? '') : '';
        const newValue = hook.value.trim();
        if (newValue !== (originalValue as string).trim()) {
          payload[key] = newValue;
        }
      }

      if (previewUrl) {
        payload['profilePicture'] = uploadedUrl ?? '';
      }

      await HuddleClientService.updateClientContact(payload, data.contactId);
      await fetchContacts?.(data.accountId, true);
      handleToggleDrawer();
      completeOperation?.('rename', 'Contact details updated successfully.');
    } catch (error) {
      completeOperation?.('rename', 'Failed to update contact details.', true);
    }
  };

  const handleAddContact = async () => {
    try {
      startOperation?.('upload', 'Adding new contact...');
      const url = await uploadProfilePicture();
      const payload = Object.entries(inputHooks).reduce(
        (acc, [key, hook]) => {
          acc[key] = hook.value.trim();
          return acc;
        },
        {} as Record<string, string>,
      );

      payload['accountId'] = accountId;
      payload['birthdate'] = birthdate ? birthdate.toISOString() : '';
      if (url) {
        payload['profilePicture'] = url;
      } else {
        payload['profilePicture'] = '';
      }

      await HuddleClientService.postClientContact(payload);
      await fetchContacts?.(accountId);
      handleToggleDrawer();
      completeOperation?.('upload', 'Contact added successfully.');
    } catch (error) {
      completeOperation?.('upload', 'Failed to add contact.', true);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        height: '100%',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          gap: '24px',
          width: '100%',
          flexDirection: 'column',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: '16px',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '80px',
              height: '80px',
              borderRadius: '22px',
              border: !previewUrl ? `1px dashed ${zeplinColor.Neutral9}` : '',
              overflow: 'hidden',
            }}
          >
            <img
              src={previewUrl ?? '/assets/account-name.svg'}
              alt='account icon'
              width={previewUrl ? 80 : 35}
              height={previewUrl ? 80 : 35}
              style={{ objectFit: 'cover' }}
            />
          </Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              flexDirection: 'column',
              gap: '12px',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <Typography variant='body2' sx={{ color: zeplinColor.BasicBlack }}>
                Profile photo
              </Typography>
              <Typography variant='label1' sx={{ color: zeplinColor.Neutral8 }}>
                JPG or PNG (max 2 MB)
              </Typography>
            </Box>
            <Box>
              <CustomButton
                disabled={false}
                onClick={openFileSelection}
                variant='contained'
                color={'primary'}
                buttonVariant='buttonSmall'
                style={{
                  borderRadius: { xs: '10px', xl: '14px' },
                  padding: '6px 12px',
                  '&.Mui-focusVisible': { backgroundColor: '#333CAA' },
                }}
                textStyle={{ color: zeplinColor.Neutral1 }}
                text={previewUrl ? 'Change Photo' : 'Add Photo'}
              />
              <input
                type='file'
                multiple
                accept='.jpeg, .jpg, .png, image/jpeg, image/png'
                ref={fileInputRef}
                style={{ display: 'none' }}
                onChange={handleFileChange}
              />
              {previewUrl && (
                <CustomButton
                  disabled={false}
                  variant='contained'
                  buttonVariant='buttonSmall'
                  onClick={clearSelection}
                  text={'Delete Photo'}
                  textStyle={{
                    color: zeplinColor.Error,
                  }}
                  style={{
                    backgroundColor: 'transparent',
                    borderRadius: { xs: '10px', xl: '14px' },
                    marginLeft: '8px',
                    padding: '6px 12px',
                    '&.Mui-focusVisible, &:hover': { backgroundColor: zeplinColor.Neutral3 },
                  }}
                />
              )}
            </Box>
          </Box>
        </Box>

        {fields.map((field) => {
          const hook = inputHooks[field.name as keyof typeof inputHooks];

          return (
            <CustomTextField
              style={{ width: '100%' }}
              onChange={hook.handleChange}
              name={field.label}
              text={hook.value}
              error={!hook.isValid && hook.didChange && `Please provide a valid ${field.name}.`}
              label={field.label}
              type='text'
              tabindex={1}
              required={field.required}
              isPhoneMask={field.name === 'phone'}
            />
          );
        })}
        <CustomDatePicker
          value={type === 'Edit Contact' && data ? dayjs(data.birthdate) : null}
          setValue={setBirthdate}
          label='Birth Date'
        />
      </Box>
      <CustomButton
        disabled={!isFormValid}
        onClick={() => {
          if (type === 'Edit Contact') {
            handleEditContact();
          }
          if (type === 'New Contact') {
            handleAddContact();
          }
        }}
        variant='contained'
        color={false ? 'customDisable' : 'primary'}
        style={{
          borderRadius: '14px',
          height: 56,
          width: '100%',
          '&.Mui-focusVisible': { backgroundColor: '#333CAA' },
        }}
        textStyle={{ color: false ? zeplinColor.Neutral8 : zeplinColor.Neutral1 }}
        text={type === 'New Contact' ? 'Add Contact' : 'Save'}
      />
    </Box>
  );
}
